/* eslint-disable @typescript-eslint/no-explicit-any */
import Box, { BoxProps } from '@mui/material/Box'
import React from 'react'

/**
 * MUI v9 removed system props as top-level JSX props on Box. Callers in this
 * codebase still pass `mt`, `gap`, `pt`, `flex`, etc. directly. We accept
 * those here and merge them into `sx` so consumers don't all need to change.
 */
const SYSTEM_SX_PROPS = [
  'm',
  'mt',
  'mb',
  'ml',
  'mr',
  'mx',
  'my',
  'p',
  'pt',
  'pb',
  'pl',
  'pr',
  'px',
  'py',
  'margin',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'padding',
  'paddingTop',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'gap',
  'rowGap',
  'columnGap',
  'flex',
  'flexDirection',
  'flexWrap',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
  'textAlign',
  'color',
  'bgcolor',
  'backgroundColor',
  'order',
  'alignSelf',
  'justifySelf',
  'display',
  'overflow',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'zIndex',
  'fontSize',
  'fontWeight',
  'lineHeight',
  'letterSpacing',
  'boxShadow',
  'borderRadius',
  'border',
  'borderTop',
  'borderBottom',
  'borderLeft',
  'borderRight',
  'borderColor'
] as const

export interface FlexBoxProps extends Omit<
  BoxProps,
  (typeof SYSTEM_SX_PROPS)[number]
> {
  children?: any
  center?: boolean
  justifyCenter?: boolean
  justifyStart?: boolean
  justifyEnd?: boolean
  alignStart?: boolean
  alignEnd?: boolean
  alignCenter?: boolean
  spaceBetween?: boolean
  direction?: 'row' | 'column' | string
  column?: boolean
  columnMd?: boolean
  columnDownMd?: boolean
  fullWidth?: boolean
  fullScreen?: boolean
  scrollable?: boolean
  absolute?: boolean
  fixed?: boolean
  bgColor?: string
  radius?: number
  styles?: any
  sx?: any
  justifyRight?: boolean
  justifyLeft?: boolean
  alignLeft?: boolean
  alignRight?: boolean
  // Re-declare system props as `any` so callers still compile
  m?: any
  mt?: any
  mb?: any
  ml?: any
  mr?: any
  mx?: any
  my?: any
  p?: any
  pt?: any
  pb?: any
  pl?: any
  pr?: any
  px?: any
  py?: any
  margin?: any
  marginTop?: any
  marginBottom?: any
  marginLeft?: any
  marginRight?: any
  padding?: any
  paddingTop?: any
  paddingBottom?: any
  paddingLeft?: any
  paddingRight?: any
  gap?: any
  rowGap?: any
  columnGap?: any
  flex?: any
  flexDirection?: any
  flexWrap?: any
  flexGrow?: any
  flexShrink?: any
  flexBasis?: any
  width?: any
  height?: any
  minWidth?: any
  maxWidth?: any
  minHeight?: any
  maxHeight?: any
  textAlign?: any
  color?: any
  bgcolor?: any
  backgroundColor?: any
  order?: any
  alignSelf?: any
  justifySelf?: any
  display?: any
  overflow?: any
  position?: any
  top?: any
  right?: any
  bottom?: any
  left?: any
  zIndex?: any
  fontSize?: any
  fontWeight?: any
  lineHeight?: any
  letterSpacing?: any
  boxShadow?: any
  borderRadius?: any
  border?: any
  borderTop?: any
  borderBottom?: any
  borderLeft?: any
  borderRight?: any
  borderColor?: any
}

const FlexBox = ({
  children,
  center,
  spaceBetween,
  justifyCenter,
  justifyStart,
  justifyEnd,
  alignStart,
  alignEnd,
  alignCenter,
  direction,
  column,
  columnMd,
  columnDownMd,
  fullWidth,
  fullScreen,
  scrollable,
  absolute,
  fixed,
  bgColor,
  radius,
  styles,
  sx,
  className,
  ...rest
}: FlexBoxProps) => {
  const classes = [
    'relative flex',
    justifyStart ? 'justify-start' : '',
    justifyEnd ? 'justify-end' : '',
    justifyCenter ? 'justify-center' : '',
    spaceBetween ? 'justify-between' : '',
    alignStart ? 'items-start' : '',
    alignEnd ? 'items-end' : '',
    alignCenter ? 'items-center' : '',
    center ? 'justify-center items-center' : '',
    column ? 'flex-col' : '',
    fullWidth ? 'w-full' : '',
    fullScreen ? 'w-full h-full' : '',
    scrollable ? 'overflow-y-scroll' : '',
    absolute ? 'absolute' : '',
    fixed ? 'fixed' : '',
    columnMd ? 'md:flex-col' : '',
    columnDownMd ? 'max-md:flex-col' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ')

  // Extract any legacy MUI system props from rest and fold them into sx.
  const systemSx: Record<string, any> = {}
  const boxRest: Record<string, any> = {}
  for (const [key, value] of Object.entries(rest)) {
    if (value === undefined) continue
    if ((SYSTEM_SX_PROPS as readonly string[]).includes(key)) {
      systemSx[key] = value
    } else {
      boxRest[key] = value
    }
  }

  const inlineStyle: React.CSSProperties = {
    ...(direction
      ? { flexDirection: direction as React.CSSProperties['flexDirection'] }
      : {}),
    ...(bgColor ? { backgroundColor: bgColor } : {}),
    ...(radius ? { borderRadius: `${radius}px` } : {}),
    ...(styles || {})
  }

  const mergedSx = Array.isArray(sx)
    ? [systemSx, ...sx]
    : sx
      ? [systemSx, sx]
      : Object.keys(systemSx).length
        ? systemSx
        : undefined

  return (
    <Box
      className={classes}
      style={inlineStyle}
      sx={mergedSx}
      {...(boxRest as BoxProps)}
    >
      {children}
    </Box>
  )
}

export default React.memo(FlexBox)
